import clsx from 'clsx';
import React, { CSSProperties } from 'react';
import ReactHtmlParser from 'react-html-parser';

import CustomSpinner, { CustomSpinnerColor } from '@components/Spinner/CustomSpinner';

import Button, { ButtonProps } from '../index';

export type ButtonSize = 'xsmall' | 'small' | 'big';
export type ButtonType = 'externalLink' | 'link' | 'button';

type VariantButtonProps = ButtonProps & {
  as: ButtonType;
  fullWidth?: boolean;
  isLoading?: boolean;
  size?: ButtonSize;
  ref?: React.MutableRefObject<HTMLAllCollection>;
};

export const CUSTOM_SPINNER_STYLES: CSSProperties = {
  display: 'flex',
  height: '15px',
  left: '50%',
  position: 'absolute',
  top: '50%',
  transform: 'translate3d(-50%, -50%, 0)',
};

const renderButtonChildren = ({
  children,
  isLoading,
  spinnerColor,
}: {
  children: React.ReactNode;
  isLoading: boolean;
  spinnerColor: CustomSpinnerColor;
}) => {
  const childrenJSX = typeof children === 'string' ? ReactHtmlParser(children) : children;
  if (isLoading) {
    return (
      <>
        <CustomSpinner color={spinnerColor} style={CUSTOM_SPINNER_STYLES} />
        <span className="invisible">{childrenJSX}</span>
      </>
    );
  }

  return childrenJSX;
};

// VariantButton is not meant to be used directly
const VariantButton = ({ className, isLoading, fullWidth, ...rest }: VariantButtonProps) => {
  return (
    <Button
      className={clsx(
        className,
        'relative flex cursor-pointer justify-center border border-solid font-suisse-intl transition-colors focus:outline-none focus-visible:ring-4 disabled:cursor-not-allowed',
        {
          'w-fit max-sm:w-full': !fullWidth,
          'w-full': fullWidth,
          'disabled:opacity-50': !isLoading,
        }
      )}
      {...rest}
    />
  );
};

export const PrimaryButton = ({
  className,
  isLoading,
  size = 'big',
  ...rest
}: VariantButtonProps) => {
  return (
    <VariantButton
      className={clsx(
        className,
        'whitespace-nowrap border-primary-magenta bg-primary-magenta text-white focus:border-primary-pink focus:bg-primary-pink focus:text-primary-magenta',
        {
          'px-8 py-4 text-button-sm': size === 'xsmall',
          'px-16 py-8 text-button-sm': size === 'small',
          'px-36 py-12 text-button-lg': size === 'big',
          'enabled:hover:border-primary-pink enabled:hover:bg-primary-pink enabled:hover:text-primary-magenta':
            rest.as === 'button',
          'hover:border-primary-pink hover:bg-primary-pink hover:text-primary-magenta':
            rest.as !== 'button',
          // only show disabled colors if not showing loading spinner
          'disabled:border-grayscale-gray disabled:bg-grayscale-gray': !isLoading,
          'pointer-events-none': isLoading && rest.as === 'link',
        }
      )}
      {...rest}
    >
      {renderButtonChildren({
        children: rest.children,
        isLoading,
        spinnerColor: CustomSpinnerColor.White,
      })}
    </VariantButton>
  );
};

export const SecondaryButton = ({
  className,
  isLoading,
  size = 'big',
  ...rest
}: VariantButtonProps) => {
  return (
    <VariantButton
      className={clsx(
        className,
        'whitespace-nowrap border-primary-magenta bg-transparent text-primary-magenta focus:bg-primary-pink',
        {
          'px-16 py-8 text-button-sm': size === 'small',
          'px-36 py-12 text-button-lg': size === 'big',
          'enabled:hover:bg-primary-pink': rest.as === 'button',
          'hover:bg-primary-pink': rest.as !== 'button',
          // only show disabled colors if not showing loading spinner
          'disabled:border-grayscale-gray disabled:bg-grayscale-gray': !isLoading,
          'pointer-events-none': isLoading && rest.as === 'link',
        }
      )}
      {...rest}
    >
      {renderButtonChildren({
        children: rest.children,
        isLoading,
        spinnerColor: CustomSpinnerColor.PrimaryMagenta,
      })}
    </VariantButton>
  );
};

export const TextButton = ({ className, size = 'big', ...rest }: VariantButtonProps) => {
  return (
    <VariantButton
      className={clsx(
        className,
        'border-transparent bg-transparent text-primary-black underline focus:text-primary-magenta',
        {
          'text-button-sm': size === 'small',
          'text-button-lg': size === 'big',
          'enabled:hover:text-primary-magenta': rest.as === 'button',
          'hover:text-primary-magenta': rest.as !== 'button',
        }
      )}
      {...rest}
    />
  );
};

export const ProButton = ({ className, isLoading, size = 'big', ...rest }: VariantButtonProps) => {
  return (
    <VariantButton
      className={clsx(
        className,
        'whitespace-nowrap border-pro-gold bg-pro-gold text-white visited:text-white focus:border-grayscale-beige focus:bg-grayscale-beige focus:text-pro-gold',
        {
          'px-8 py-4 text-button-sm': size === 'xsmall',
          'px-16 py-8 text-button-sm': size === 'small',
          'px-36 py-12 text-button-lg': size === 'big',
          'enabled:hover:border-grayscale-beige enabled:hover:bg-grayscale-beige enabled:hover:text-pro-gold':
            rest.as === 'button',
          'hover:border-grayscale-beige hover:bg-grayscale-beige hover:text-pro-gold':
            rest.as !== 'button',
          // only show disabled colors if not showing loading spinner
          'disabled:border-grayscale-gray disabled:bg-grayscale-gray': !isLoading,
          'pointer-events-none': isLoading && rest.as === 'link',
        }
      )}
      {...rest}
    >
      {renderButtonChildren({
        children: rest.children,
        isLoading,
        spinnerColor: CustomSpinnerColor.White,
      })}
    </VariantButton>
  );
};
